// 1. React
import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";

// 2. Helpers
import { classNames, useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";
import { getSettings } from "services/api/settings/redux/selectors";
import { useHandleTextFieldChange } from "services/forms/hooks";

// 3. Types
import { VehicleOfferSaleInfoProps } from "./types";
import { FleetVehicleOfferSale, FleetVehicleOfferSaleEditFormValues } from "services/api/fleet/vehicles/offers/types";
import { Errors } from "services/forms/validation/types";

// 4. API / Consts / Transformations
import {
  createFleetVehicleOfferSaleAPI,
  getFleetVehicleOfferSaleAPI,
  getFleetVehicleOfferSalePdfPreviewAPI,
  getOfferSaleGenerateNumberAPI,
  updateFleetVehicleOfferSaleAPI,
} from "services/api/fleet/vehicles/offers/api";
import { initialFleetVehicleOfferSaleEditForm, OfferSaleStatusId } from "services/api/fleet/vehicles/offers/consts";
import { transformFleetVehicleOfferSaleEditForm } from "services/api/fleet/vehicles/offers/transformations";

// 5. UI Components
import { Button } from "ui/components/Button/Button";
import { Alert } from "ui/components/Alert";
import { Dialog } from "ui/components/Dialog";
import { PdfViewer } from "ui/components/PdfViewer";
import { TextArea } from "ui/components/TextField/TextArea";

// 6. Validation
import { FLEET_VEHICLE_OFFER_SALE_EDIT_FORM_SCHEMA } from "./validation";

// 7. Local Components
import OfferHeader from "./components/OfferHeader/OfferHeader";
import OfferItems from "./components/OfferItems/OfferItems";
import OfferTotal from "./components/OfferTotal/OfferTotal";
import OfferEmailModal from "./components/OfferEmailModal/OfferEmailModal";
import PaymentQrModal from "./components/PaymentQrModal/PaymentQrModal";





const VehicleOfferSaleInfo: React.FC<VehicleOfferSaleInfoProps> = (props) => {
  const { vehicleId, offerId, onStatusHistoryChange, currentStatusId, onFinish, onCreateContract, setIsLoading, onClose, hasIntro, pdfUrl, setPdfUrl, showPdf, setShowPdf } = props;

  const { t } = useTranslate();
  const settings = useSelector(getSettings);

  const [formValues, setFormValues] =
    useState<FleetVehicleOfferSaleEditFormValues>(
      initialFleetVehicleOfferSaleEditForm
    );

  const [errors, setErrors] = useState<Errors>({});

  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [showPaymentQrModal, setShowPaymentQrModal] = useState<boolean>(false);
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);

  useEffect(() => {
    if (!vehicleId) return;

    setFormValues(prev => ({
      ...prev,
      vehicleId,
    }));
  }, [vehicleId]);

  // Sync status kad se promijeni putem toolbara u parent komponenti
  useEffect(() => {
    if (currentStatusId) {
      setFormValues(prev => ({ ...prev, offerSaleStatusId: currentStatusId }));
    }
  }, [currentStatusId]);

  useEffect(() => {

    if (offerId) {
      fetchOfferSale(offerId);
    }
    else {
      fetchNewOfferNumber();
    }
  }, [offerId]);

  const fetchNewOfferNumber = async () => {
    try {
      let newOfferNumber = await getOfferSaleGenerateNumberAPI();
      if (newOfferNumber) {
        setFormValues(prev => {
          let dueDate: Date | null = null;
          if (settings.offerSaleDurationDays) {
            const date = new Date();
            date.setDate(date.getDate() + settings.offerSaleDurationDays);
            dueDate = date;
          }

          return {
            ...prev,
            offerNumber: newOfferNumber,
            durationDays: settings.offerSaleDurationDays ?? prev.durationDays,
            dueDate: dueDate ?? prev.dueDate,
            note: settings.offerSaleNoteDefault ?? prev.note,
          };
        });
      }
    } catch {
      // error handled by snackbar
    }
    setErrors({});
  };

  const fetchOfferSale = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleOfferSaleEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleOfferSaleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(transformFleetVehicleOfferSaleEditForm(result));
    onStatusHistoryChange(result.statusHistory ?? [], result.id, result.status?.id);
    if (result.pdfUpload?.url) {
      setPdfUrl(result.pdfUpload.url);
      setShowPdf(true);
    }
    setIsLoading(false);

    setErrors({});
  };

  // Save as draft and show PDF preview
  const handleSaveDraft = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_OFFER_SALE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      const saveFormValues = {
        ...formValues,
        offerSaleStatusId: formValues.id ? formValues.offerSaleStatusId : OfferSaleStatusId.Draft,
      };
      let result: FleetVehicleOfferSale | undefined;

      if (formValues.id) {
        result = await updateFleetVehicleOfferSaleAPI(formValues.id, saveFormValues, t);
      } else if (vehicleId) {
        result = await createFleetVehicleOfferSaleAPI(vehicleId, saveFormValues, t);
      }

      if (result) {
        const { id: newId, items: newItems } = result;
        setFormValues(prev => ({ ...prev, id: newId, offerSaleStatusId: result!.status?.id ?? prev.offerSaleStatusId, items: newItems }));
        onStatusHistoryChange(result.statusHistory ?? [], result.id, result.status?.id);

        if (result?.pdfUpload?.url) {
          setPdfUrl(result?.pdfUpload?.url);
        }
      }
    } catch (e) {
      // error handled by snackbar
    }
    setIsLoading(false);
  };

  // Create offer from draft (change status to created)
  // Kreiraj ponudu iz predloška — mijenja status u "kreiran", prikazuje overlay uspjeha + PDF
  const handleCreateOffer = async (e: any) => {
    e.preventDefault();

    if (!formValues.id) return;

    setIsLoading(true);

    try {
      const createdFormValues = { ...formValues, offerSaleStatusId: OfferSaleStatusId.Created };
      const result = await updateFleetVehicleOfferSaleAPI(formValues.id, createdFormValues, t);

      if (result) {
        setFormValues(prev => ({ ...prev, offerSaleStatusId: OfferSaleStatusId.Created }));
        onStatusHistoryChange(result.statusHistory ?? [], result.id, result.status?.id);
        setIsLoading(false);

        // Prikaži overlay uspješnog kreiranja
        if (onFinish) onFinish();

        // Odmah dohvati i prikaži PDF pregled (isti tok kao kod ugovora)
        if (result.pdfUpload?.url) {
          setPdfUrl(result.pdfUpload.url);
          setShowPdf(true);
        }
      }
    } catch (e) {
      // greška se obrađuje putem snackbara
    }
    setIsLoading(false);
  };

  const handleCancelOffer = (e: any) => {
    e.preventDefault();
    setOpenCancelDialog(true);
  };

  const handleCancelDialogConfirm = async () => {
    setOpenCancelDialog(false);
    if (!formValues.id) return;

    setIsLoading(true);
    try {
      const cancelledFormValues = { ...formValues, offerSaleStatusId: OfferSaleStatusId.Cancelled };
      const result = await updateFleetVehicleOfferSaleAPI(formValues.id, cancelledFormValues, t);

      if (result) {
        setFormValues(prev => ({ ...prev, offerSaleStatusId: OfferSaleStatusId.Cancelled }));
        onStatusHistoryChange(result.statusHistory ?? [], result.id, result.status?.id);
      }
    } catch {
      // error handled by snackbar
    }
    setIsLoading(false);
  };


  const handlePreviewPdf = async (e: any) => {
    e.preventDefault();

    const fileName = `${formValues.offerNumber ?? "Ponuda"}-Ponuda.pdf`;
    const file = await getFleetVehicleOfferSalePdfPreviewAPI(formValues.id!, fileName);
    if (file?.url) {
      setPdfUrl(file.url);
      setShowPdf(true);
    }

  }

  const handlePrintPdf = async (e: any) => {
    e.preventDefault();

    const fileName = `${formValues.offerNumber ?? "Ponuda"}-Ponuda.pdf`;
    const file = await getFleetVehicleOfferSalePdfPreviewAPI(formValues.id!, fileName);
    if (file?.url) {
      const printWindow = window.open(file.url, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      }
    }
  }

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const items = formValues.items.filter(i => !i.deleted);
  const offerTotal = items.reduce((sum, item) => sum + (item.total ?? 0), 0);
  const isCancelled = formValues.offerSaleStatusId === OfferSaleStatusId.Cancelled;

  return (
    <>
      <>
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
            <p className="text-red-800 font-medium text-center">Ponuda je stornirana</p>
          </div>
        )}
        <div className={isCancelled ? "opacity-50 pointer-events-none" : ""}>
          <OfferHeader
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
          />
          <div className="">
            <div className="px-2 px-4 py-2 text-sm font-medium transition-colors duration-200 border-b-2  border-sky-600 text-sky-600 ">Stavke ponude</div>
            <div className="border-x border-b border-slate-300  rounded-b-md">
              <OfferItems
                formValues={formValues}
                setFormValues={setFormValues}
              />
              <OfferTotal
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </div>
          </div>
          <div className="mb-3 mt-3">
            <TextArea
              name="note"
              label={t("admin.fleet.vehicle.offer.sale.note")}
              placeholder={t("admin.fleet.vehicle.offer.sale.note.placeholder")}
              value={formValues.note}
              onChange={handleTextFieldChange}
            />
          </div>
        </div>
        {errors && errors["items"] !== undefined && items.length === 0 ? (
          <Alert title="Važno!" message={errors["items"]} variant="danger" />
        ) : <></>}
        {!isCancelled && (
          <div className={classNames(
            "px-4 py-3 fixed bottom-0 bg-slate-50 border border-t-slate-200 flex justify-between gap-3",
            hasIntro ? "right-0 w-1/2" : "left-0 w-full"
          )}>
            <div className="flex gap-2">
              <Button
                variant="outlined"
                children={t("admin.close")}
                onClick={onClose}
              />
              {formValues.id && formValues.offerSaleStatusId !== OfferSaleStatusId.Draft && (
                <Button
                  variant="remove"
                  children="Storniraj ponudu"
                  onClick={handleCancelOffer}
                />
              )}
              {formValues.id && !showPdf && (
                <Button
                  startIcon="/media/icons/search.svg"
                  variant="secondary"
                  children={t("admin.preview.pdf")}
                  onClick={handlePreviewPdf}
                />
              )}
              {formValues.id && formValues.offerSaleStatusId !== OfferSaleStatusId.Draft && showPdf && (
                <Button
                  startIcon="/media/icons/print.svg"
                  variant="secondary"
                  children="Ispiši ponudu"
                  onClick={handlePrintPdf}
                />
              )}
              {formValues.id && formValues.offerSaleStatusId !== OfferSaleStatusId.Draft && (
                <Button
                  startIcon="/media/icons/email.svg"
                  variant="secondary"
                  children="Pošalji ponudu"
                  onClick={() => setShowEmailModal(true)}
                />
              )}
              {formValues.id && [OfferSaleStatusId.Sent, OfferSaleStatusId.Accepted, OfferSaleStatusId.Paid].includes(formValues.offerSaleStatusId as any) && (
                <Button
                  startIcon="/media/icons/email.svg"
                  variant="outlined"
                  children="Pošalji na plaćanje"
                  onClick={() => setShowPaymentQrModal(true)}
                />
              )}
              
            </div>
            <div className="flex gap-2">
              {formValues.id && formValues.offerSaleStatusId !== OfferSaleStatusId.Draft && onCreateContract && (
                <Button
                  variant="secondary"
                  children="Kreiraj ugovor"
                  onClick={() => onCreateContract(formValues.id!)}
                />
              )}
              <Button
                variant="primary"
                children={
                  !formValues.id
                    ? "Spremi predložak"
                    : formValues.offerSaleStatusId === OfferSaleStatusId.Draft
                      ? "Kreiraj ponudu"
                      : "Spremi ponudu"
                }
                onClick={
                  formValues.id && formValues.offerSaleStatusId === OfferSaleStatusId.Draft
                    ? handleCreateOffer
                    : handleSaveDraft
                }
              />
            </div>
          </div>
        )}

        {pdfUrl && (
          <PdfViewer
            url={pdfUrl}
            show={showPdf}
          />
        )}
      </>

      <OfferEmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        formValues={formValues}
      />

      <PaymentQrModal
        show={showPaymentQrModal}
        onHide={() => setShowPaymentQrModal(false)}
        formValues={formValues}
        total={offerTotal}
      />

      <Dialog
        open={openCancelDialog}
        title={t("admin.fleet.vehicle.offer.sale.dialog.cancel.title")}
        message={t("admin.fleet.vehicle.offer.sale.dialog.cancel.message")}
        confirmText={t("admin.fleet.vehicle.offer.sale.dialog.cancel.confirmtext")}
        cancelText={t("admin.fleet.vehicle.offer.sale.dialog.cancel.canceltext")}
        onConfirm={handleCancelDialogConfirm}
        onCancel={() => setOpenCancelDialog(false)}
      />
    </>
  );
};

export default memo(VehicleOfferSaleInfo);
